import { getProductSchemaConfig } from './productSchemaConfig';

// Static nodes shared across all pricing pages — must match hubspot.com exactly
// so Google links them into the same knowledge graph entities.
const ORGANIZATION = {
  '@type': 'Organization',
  '@id': 'https://www.hubspot.com/#organization',
  name: 'HubSpot',
  url: 'https://www.hubspot.com',
  logo: {
    '@type': 'ImageObject',
    url: 'https://www.hubspot.com/hubfs/HubSpot_Logos/HSLogo_color.svg'
  },
  sameAs: ['https://www.wikidata.org/wiki/Q5926631', 'https://en.wikipedia.org/wiki/HubSpot', 'https://www.linkedin.com/company/hubspot', 'https://x.com/HubSpot', 'https://www.instagram.com/hubspot/', 'https://www.facebook.com/hubspot/', 'https://www.tiktok.com/@HubSpot']
};
const WEBSITE = {
  '@type': 'WebSite',
  '@id': 'https://www.hubspot.com/#website',
  url: 'https://www.hubspot.com/',
  name: 'HubSpot',
  inLanguage: 'en',
  publisher: {
    '@id': 'https://www.hubspot.com/#organization'
  }
};
function buildOffers(cards, currencyCode, pricingUrl) {
  const offers = [];
  for (const card of cards) {
    // Iterate all available terms for this card so all pricing variants appear
    // in the schema — not just the user's currently selected term.
    for (const option of Object.values(card.availableTerms)) {
      const monthlyPrice = parseFloat(option.monthlyPrice);
      if (isNaN(monthlyPrice) || monthlyPrice <= 0) continue;
      const isAcap = option.commitmentTerm === 'ANNUAL' && option.paymentTerm === 'ANNUAL';
      const isAcmp = option.commitmentTerm === 'ANNUAL' && option.paymentTerm === 'MONTHLY';

      // ACAP: Google treats the annual total as the price for a license, not the
      // monthly breakdown — use the full year amount with P1Y billing duration.
      // All other terms (ACMP, monthly) stay as monthly price with P1M.
      const priceStr = isAcap ? (monthlyPrice * 12).toFixed(2) : monthlyPrice.toFixed(2);
      const billingDuration = isAcap ? 'P1Y' : 'P1M';
      let termSuffix = ' - Monthly Billing';
      if (isAcmp) termSuffix = ' - Annual Commitment, Monthly Billing';
      if (isAcap) termSuffix = ' - Annual Commitment, Annual Billing';
      offers.push({
        '@type': 'Offer',
        name: `${card.displayName}${termSuffix}`,
        price: priceStr,
        priceCurrency: currencyCode,
        url: pricingUrl,
        availability: 'https://schema.org/InStock',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: priceStr,
          priceCurrency: currencyCode,
          billingDuration
        }
      });
    }
  }
  return offers;
}
export function buildPageSchema({
  productGroup,
  cards,
  currencyCode,
  locale
}) {
  var _document$querySelect, _document$querySelect2, _document$querySelect3, _document$querySelect4;
  const config = getProductSchemaConfig(productGroup);
  if (!config) return null;

  // Canonical URL for this page — the HTML template sets a <link rel="canonical">
  // that already encodes the correct locale-specific domain (e.g. hubspot.de for de).
  const pricingUrl = (_document$querySelect = (_document$querySelect2 = document.querySelector('link[rel="canonical"]')) === null || _document$querySelect2 === void 0 ? void 0 : _document$querySelect2.href.split(/[?#]/)[0]) !== null && _document$querySelect !== void 0 ? _document$querySelect : window.location.href.split(/[?#]/)[0];
  const webPageId = `${pricingUrl}#webpage`;
  const pageDescription = (_document$querySelect3 = (_document$querySelect4 = document.querySelector('meta[name="description"]')) === null || _document$querySelect4 === void 0 ? void 0 : _document$querySelect4.content) !== null && _document$querySelect3 !== void 0 ? _document$querySelect3 : undefined;
  const webPage = Object.assign({
    '@type': 'WebPage',
    '@id': webPageId,
    url: pricingUrl,
    name: document.title
  }, pageDescription ? {
    description: pageDescription
  } : {}, {
    inLanguage: locale,
    isPartOf: {
      '@id': 'https://www.hubspot.com/#website'
    },
    about: {
      '@id': config.productId
    }
  });
  const offers = buildOffers(cards, currencyCode, pricingUrl);
  if (offers.length === 0) return null;
  const product = {
    '@type': ['Product', 'SoftwareApplication'],
    '@id': config.productId,
    name: config.name,
    offers
  };
  return {
    '@context': 'https://schema.org',
    '@graph': [ORGANIZATION, WEBSITE, webPage, product]
  };
}